import {Component, OnInit} from '@angular/core';
import {BehaviorSubject, combineLatest, Observable, of} from 'rxjs';
import {
  CustomerContact,
  CustomerSourcingService,
  InsuranceCategoryFamily,
  InsuranceService,
  InsuranceSubscription,
  InsuranceSubscriptionSearch,
  InsuranceSubscriptionService,
  SearchResult,
  SimplePaginationUtils, Ticket, TicketType,
} from '@lifeislife/lifeislife-domain';
import {CustomerContextService} from '../../service/customer-context-service';
import {ActivatedRoute} from '@angular/router';
import {debounceTime, distinctUntilChanged, filter, map, publishReplay, refCount, switchMap, take} from 'rxjs/operators';
import {LazyLoadEvent} from 'primeng/api';
import {isSameRef} from '../../service/utils';
import {PendingTicketDraftService} from '../../service/pending-ticket-draft.service';
import {CustomerInsuranceInformationRequestHelperService} from '../../service/customer-insurance-information-request-helper.service';

@Component({
  selector: 'cf-product-family-route',
  templateUrl: './product-family-route.component.html',
  styleUrls: ['./product-family-route.component.scss'],
})
export class ProductFamilyRouteComponent implements OnInit {

  InsuranceCategoryFamily = InsuranceCategoryFamily;
  insuranceFamily$: Observable<InsuranceCategoryFamily>;
  familyLabel$: Observable<string>;
  insuranceResults$: Observable<SearchResult<InsuranceSubscription>>;
  insuranceLoadSource$ = new BehaviorSubject<any>(true);
  pagination = SimplePaginationUtils.newPagination(50);

  sendingInProgress$ = new BehaviorSubject<boolean>(false);
  sourcingRequestSent: boolean;
  sourcingRequestErrored: boolean;

  pendingInsuranceInfoRequestDraft$: Observable<Ticket | null>;
  editingInsuranceInfoReaquestDraf$: Observable<Ticket | null>;

  constructor(
    private activatedRoute: ActivatedRoute,
    private sourcingService: CustomerSourcingService,
    private customerContextService: CustomerContextService,
    private insuranceService: InsuranceService,
    private insuranceSubscriptionService: InsuranceSubscriptionService,
    private pendingTicketDraftService: PendingTicketDraftService,
    private customerInsuranceInformationRequestHelperService: CustomerInsuranceInformationRequestHelperService,
  ) {
  }

  ngOnInit(): void {
    this.insuranceFamily$ = this.activatedRoute.data.pipe(
      map(d => d.insuranceCategoryFamily),
      publishReplay(1), refCount(),
    );

    this.familyLabel$ = this.insuranceFamily$.pipe(
      map(i => i == null ? '' : this.insuranceService.getInsuranceFamilyLabel(i)),
      publishReplay(1), refCount(),
    );

    this.insuranceResults$ = combineLatest([
      this.insuranceFamily$, this.insuranceLoadSource$,
    ]).pipe(
      debounceTime(0),
      map(r => r[0]),
      switchMap(i => this.searchSubscription$(i)),
      publishReplay(1), refCount(),
    );

    this.pendingInsuranceInfoRequestDraft$ = this.pendingTicketDraftService.getPendingDraft$(TicketType.CUSTOMER_INSURANCE_REQUEST);
    this.editingInsuranceInfoReaquestDraf$ = this.customerInsuranceInformationRequestHelperService.editingTicketDraft$;
  }

  onPageChange(event: LazyLoadEvent) {
    this.pagination = SimplePaginationUtils.applyLazyLoadEvent(this.pagination, event);
    this.insuranceLoadSource$.next(true);
  }

  onMoreClick() {
    this.sourcingRequestErrored = false;
    this.sourcingRequestSent = false;
    this.sendingInProgress$.next(true);

    this.insuranceFamily$.pipe(
      take(1),
    ).subscribe(family => this.sendSourcingRequest(family));
  }

  private sendSourcingRequest(family: InsuranceCategoryFamily) {
    const customerRef = this.customerContextService.getActiveCustomerRef();
    const contactRef = this.customerContextService.getActiveContactRef();


    this.sourcingService.notifyPotentialCustomerFromCustomerFront$({
      customerRef: customerRef,
      contactRef: contactRef,
      family: family,
    }).subscribe(
      () => this.onSourcingRequetsSent(),
      e => this.onSourcingRequestError(e),
    );
  }

  private onSourcingRequetsSent() {
    this.sendingInProgress$.next(false);
    this.sourcingRequestSent = true;
  }

  private onSourcingRequestError(error: any) {
    this.sendingInProgress$.next(false);
    this.sourcingRequestSent = false;
    this.sourcingRequestErrored = true;
    console.warn(error);
  }


  private searchSubscription$(family: InsuranceCategoryFamily) {
    return this.customerContextService.activeCustomerContact$.pipe(
      distinctUntilChanged((ca, cb) => isSameRef(ca, cb)),
      switchMap(c => this.searchCustomerContactsubscriptions$(c, family)),
    );
  }

  private searchCustomerContactsubscriptions$(customerContact: CustomerContact, family: InsuranceCategoryFamily): Observable<SearchResult<InsuranceSubscription>> {
    if (customerContact == null) {
      return of(new SearchResult<InsuranceSubscription>());
    }

    const insuranceSubscriptionSearch: InsuranceSubscriptionSearch = {
      insuranceSearch: {
        categorySearch: {
          anyFamily: [family],
        },
      },
      insuranceSubscriptionStatusSearch: {
        allVisibleToCustomers: true,
      },
      subscriptionSearch: {
        archived: false,
        customerSearch: {
          exactCustomerRef: customerContact.customerRef,
        },
      },
    };

    return this.insuranceSubscriptionService.searchInsuranceSubscriptions$(insuranceSubscriptionSearch, this.pagination);
  }

  onNewSourcingInfoRequestClick() {
    this.insuranceFamily$.pipe(
      take(1),
      switchMap(f => this.customerInsuranceInformationRequestHelperService.createDraft$(f)),
    ).subscribe();
  }
}
